import { api } from "@vuumly-common/common";
import { ChartData, ChartOptions, TooltipItem } from "chart.js";
import { useFormatNumber, useQuery, useTheme, useTranslate } from "hooks";
import _ from "lodash";
import { useMemo } from "react";
import { RoutePathEnum } from "../../../../controllers/router";
import { BaseBarChart } from "../../../Charts";
import { TextH5 } from "../../../Typography/Text";
import { BaseCard } from "../../Base.card";
import { CardProps } from "../../index";

export const RideLengthHourCompanies = (props: CardProps) => {
  const { disableLink = true } = props;
  const { formatShort } = useFormatNumber();
  const { getCompanyColors } = useTheme();

  const translate = useTranslate();

  const { data, isLoading } = useQuery({
    apiEndpoint: api.Endpoint.RideLengthHourCompanies,
  });

  const clonedData = useMemo(() => {
    if (isLoading || !data?.datasets) {
      return;
    }
    const parsedData = _.cloneDeep(data) as ChartData<"bar", Record<any, any>>;

    parsedData.labels = parsedData.datasets.flatMap((obj) =>
      obj.data
        .filter((item: any) => "ride_length_hour" in item)
        .map((item: any) => item.ride_length_hour)
    );

    parsedData.labels = _.uniq(parsedData.labels);

    parsedData.datasets.forEach((dataset) => {
      const dates = dataset.data.map((item: any) => item.ride_length_hour);

      dataset.data =
        parsedData.labels?.map((item: any) => {
          if (!dates.includes(item)) {
            return { date: item, ride_count: 0 };
          } else {
            return dataset.data.find(
              (data: any) => data.ride_length_hour === item
            );
          }
        }) || [];

      dataset.label = `Company ${dataset.dimensions?.company_id}`;

      dataset.order = -dataset.data.reduce(
        (acc: number, item: any) => item.ride_count + acc,
        0
      );

      const colors = getCompanyColors(dataset.dimensions?.company_id);

      dataset.backgroundColor = colors.color;
      dataset.hoverBackgroundColor = colors.color;

      dataset.borderColor = "transparent";
      dataset.borderWidth = {
        top: 2,
      };
    });

    return parsedData;
  }, [data]);

  const options: ChartOptions<"line"> = {
    parsing: {
      xAxisKey: "ride_length_hour",
      yAxisKey: "ride_count",
    },

    elements: {
      bar: {
        borderRadius: 0,
      },
    },

    scales: {
      x: {
        title: {
          text: "Ride length in hours",
          display: true,
        },
        stacked: true,
        ticks: {
          callback: (value, idx) => {
            if (value === 0) {
              return "Bellow 1h";
            }
            if (
              clonedData &&
              clonedData.labels &&
              clonedData.labels.length - 1 === idx
            ) {
              return `Above ${Number(value)}h`;
            }
            return `${value}-${Number(value) + 1}h`;
          },
        },
      },
      y: {
        max: 100,
        title: {
          text: "Market share",
        },
        stacked: true,

        ticks: {
          callback: (value) => {
            return `${value}%`;
          },
        },
      },
    },

    plugins: {
      stacked100: { enable: true },
      tooltip: {
        itemSort: (a: any, b: any) => {
          return b.dataset.order - a.dataset.order;
        },
        callbacks: {
          title: (item) => {
            const idx = item[0].dataIndex;
            if (idx === 0) {
              return `Rides bellow 1h`;
            }
            if (idx === 24) {
              return `Rides above 24h`;
            }

            return `Rides between ${idx}h and ${idx + 1}h`;
          },
          label: (item: TooltipItem<"line">) => {
            const raw = item.raw as Record<string, any>;
            return `Company ${
              item.dataset.dimensions?.company_id
            }: ${formatShort(raw.ride_count)} (${item.formattedValue}%)`;
          },
        },
      },
    },
  };

  const Header = () => {
    return (
      <div className="flex justify-between w-full">
        <TextH5>Market share by ride length in hours</TextH5>
      </div>
    );
  };

  return (
    <BaseCard
      isLoading={isLoading}
      header={<Header />}
      href={disableLink ? undefined : RoutePathEnum.Rides}
    >
      <BaseBarChart options={options} data={clonedData} />
    </BaseCard>
  );
};
